import { z } from 'zod';
import { isValidEan } from './stores.js';
import { detectStore } from './images.js';
export const storeKeySchema = z.enum(['x-kom', 'morele', 'media-expert', 'rtv-euro-agd', 'komputronik', 'amazon-pl']);
export const identitySchema = z.object({
    manufacturer: z.string().trim().max(120).default(''),
    model: z.string().trim().max(120).default(''),
    ean: z.string().trim().refine((value) => !value || isValidEan(value), 'Podaj poprawny EAN-8 lub EAN-13 (z cyfrą kontrolną).').default(''),
    variant: z.string().trim().max(180).default(''),
});
export const sourceSchema = z.object({
    storeKey: storeKeySchema,
    status: z.enum(['pending', 'checking', 'matched', 'not_found', 'ambiguous', 'blocked', 'error', 'identity_required']),
    offerId: z.string().uuid().nullable(),
    lastSearchedAt: z.string().datetime().nullable(),
    errorCode: z.string().nullable(),
    message: z.string().nullable(),
});
const discoveryFields = {
    identity: identitySchema.default({ manufacturer: '', model: '', ean: '', variant: '' }),
    selectedStores: z.array(storeKeySchema).max(6).refine((values) => new Set(values).size === values.length, 'Sklepy nie mogą się powtarzać.').default([]),
};
export const currencySchema = z.string().regex(/^[A-Z]{3}$/, 'Waluta musi być kodem ISO 4217');
export const uuidSchema = z.string().uuid();
const optionalAmountSchema = z.number({ error: 'Podaj cenę jako liczbę, np. 249,99.' }).int('Cena może mieć najwyżej dwa miejsca po przecinku.').positive('Cena musi być większa od zera.').nullable();
export const availabilitySchema = z.enum(['in_stock', 'out_of_stock', 'unknown']);
export const checkStatusSchema = z.enum(['ok', 'pending', 'error', 'never']);
export const monitoringStatusSchema = z.enum(['active', 'pending', 'checking', 'error', 'unsupported', 'paused']);
export const categorySchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(80),
    slug: z.string().min(1).max(80),
    icon: z.string().max(40),
    productCount: z.number().int().nonnegative(),
});
export const storeSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(120),
    domain: z.string().min(1).max(255),
});
export const pricePointSchema = z.object({
    id: uuidSchema,
    priceMinor: z.number().int().nonnegative(),
    currency: currencySchema,
    recordedAt: z.string().datetime(),
    source: z.enum(['manual', 'worker', 'demo']),
    availability: availabilitySchema.optional(),
});
export const productOfferSchema = z.object({
    id: uuidSchema,
    imageUrl: z.string().url().nullable().optional(),
    store: storeSchema,
    url: z.string().url(),
    currentPriceMinor: z.number().int().nonnegative().nullable(),
    previousPriceMinor: z.number().int().nonnegative().nullable(),
    targetPriceMinor: z.number().int().nonnegative().nullable(),
    shippingPriceMinor: z.number().int().nonnegative().nullable(),
    currency: currencySchema,
    availability: availabilitySchema,
    checkStatus: checkStatusSchema,
    monitoringStatus: monitoringStatusSchema,
    lastCheckedAt: z.string().datetime().nullable(),
    lastSuccessfulAt: z.string().datetime().nullable(),
    lastError: z.string().nullable(),
    lastErrorCode: z.string().nullable().optional(),
    matchMethod: z.enum(['manual', 'ean', 'manufacturer_model']).optional(),
    matchStatus: z.enum(['verified', 'probable', 'uncertain', 'rejected']).optional(),
    matchConfidence: z.number().min(0).max(1).nullable().optional(),
    history: z.array(pricePointSchema),
});
export const productSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(180),
    imageUrl: z.string().url().nullable(),
    note: z.string().max(1000).nullable(),
    targetPriceMinor: z.number().int().nonnegative().nullable().optional(),
    category: categorySchema,
    offers: z.array(productOfferSchema),
    ...discoveryFields,
    sources: z.array(sourceSchema).default([]),
    createdAt: z.string().datetime(),
    isDemo: z.boolean(),
});
export const createProductSchema = z.object({
    url: z.union([z.literal(''), z.string().url('Podaj poprawny adres URL').max(2048)]),
    name: z.string().trim().min(2, 'Nazwa musi mieć co najmniej 2 znaki').max(180),
    imageUrl: z.union([z.literal(''), z.string().url('Podaj poprawny URL zdjęcia')]).optional(),
    storeName: z.string().trim().max(120),
    initialPriceMinor: optionalAmountSchema,
    targetPriceMinor: optionalAmountSchema,
    currency: currencySchema.default('PLN'),
    categoryId: z.string().uuid('Wybierz kategorię dla produktu.'),
    note: z.string().trim().max(1000).optional(),
    ...discoveryFields,
}).superRefine((input, ctx) => {
    if (!input.url && !input.selectedStores.length)
        ctx.addIssue({ code: 'custom', path: ['url'], message: 'Dodaj link lub wybierz sklepy do wyszukania.' });
    if (input.url && input.storeName.length < 2)
        ctx.addIssue({ code: 'custom', path: ['storeName'], message: 'Podaj nazwę sklepu.' });
    if (input.selectedStores.length && !input.identity.ean && !(input.identity.manufacturer && input.identity.model && input.identity.variant))
        ctx.addIssue({ code: 'custom', path: ['identity'], message: 'Wyszukiwanie wymaga EAN albo producenta, dokładnego kodu modelu i wariantu.' });
});
export const updateProductSchema = z.object({
    name: z.string().trim().min(2, 'Podaj nazwę produktu (co najmniej 2 znaki).').max(180, 'Nazwa może mieć najwyżej 180 znaków.'),
    imageUrl: z.union([z.literal(''), z.string().url('Wklej poprawny link do zdjęcia.')]).optional(),
    categoryId: z.string().uuid('Wybierz kategorię dla produktu.'),
    note: z.string().trim().max(1000).optional(),
    targetPriceMinor: optionalAmountSchema,
    ...discoveryFields,
}).superRefine((input, ctx) => {
    if (input.selectedStores.length && !input.identity.ean && !(input.identity.manufacturer && input.identity.model && input.identity.variant))
        ctx.addIssue({ code: 'custom', path: ['identity'], message: 'Podaj EAN albo producenta, dokładny kod modelu i wariant.' });
});
export const attachOfferSchema = z.object({
    storeKey: storeKeySchema, url: z.string().url().max(2048),
    initialPriceMinor: optionalAmountSchema.default(null),
    confirmedVariant: z.literal(true, { error: 'Potwierdź, że link prowadzi do dokładnie tego samego wariantu produktu.' }),
});
export const dashboardSummarySchema = z.object({
    trackedProducts: z.number().int().nonnegative(),
    recentDrops: z.number().int().nonnegative(),
    reachedTargets: z.number().int().nonnegative(),
    potentialSavingsMinor: z.number().int(),
    currency: currencySchema,
});
export const dashboardSchema = z.object({
    summary: dashboardSummarySchema,
    products: z.array(productSchema),
    attentionProductIds: z.array(uuidSchema),
    marketHistory: z.array(z.object({ date: z.string(), valueMinor: z.number().int().nonnegative() })),
    generatedAt: z.string().datetime(),
    isDemo: z.boolean(),
});
export const readSourceSchema = z.enum(['official_api', 'json_ld', 'html_meta', 'store_adapter', 'browser']);
export const priceEventTypeSchema = z.enum([
    'price_drop', 'price_increase', 'target_reached', 'new_historical_minimum',
    'back_in_stock', 'out_of_stock',
]);
export const normalizedMeasurementSchema = z.object({
    offerId: uuidSchema,
    productName: z.string().min(1).max(300),
    url: z.string().url(),
    store: z.string().min(1).max(120),
    priceMinor: z.number().int().positive(),
    currency: currencySchema,
    availability: availabilitySchema,
    shippingPriceMinor: z.number().int().nonnegative().nullable(),
    checkedAt: z.string().datetime(),
    source: readSourceSchema,
    status: z.literal('success'),
});
export const failedMeasurementSchema = z.object({
    offerId: uuidSchema,
    productName: z.string().min(1).max(300),
    url: z.string().url(),
    store: z.string().min(1).max(120),
    checkedAt: z.string().datetime(),
    source: readSourceSchema.nullable(),
    status: z.enum(['error', 'unsupported', 'blocked']),
    errorCode: z.string().min(1).max(80),
    errorMessage: z.string().min(1).max(1000),
    retryable: z.boolean(),
});
export const priceEventSchema = z.object({
    id: uuidSchema,
    offerId: uuidSchema,
    type: priceEventTypeSchema,
    previousPriceMinor: z.number().int().nonnegative().nullable(),
    currentPriceMinor: z.number().int().nonnegative().nullable(),
    currency: currencySchema,
    occurredAt: z.string().datetime(),
});
export const monitoringRunSchema = z.object({
    id: uuidSchema,
    status: z.enum(['running', 'succeeded', 'partial', 'failed']),
    triggerSource: z.enum(['manual', 'github_actions', 'scheduler']),
    offersRequested: z.number().int().nonnegative(),
    offersSucceeded: z.number().int().nonnegative(),
    offersFailed: z.number().int().nonnegative(),
    eventsCreated: z.number().int().nonnegative(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
});
export const monitoringHealthSchema = z.object({
    configured: z.boolean(),
    activeOffers: z.number().int().nonnegative(),
    failingOffers: z.number().int().nonnegative(),
    unsupportedOffers: z.number().int().nonnegative(),
    message: z.string().optional(),
    lastRun: monitoringRunSchema.pick({ id: true, status: true, startedAt: true, finishedAt: true, offersSucceeded: true, offersFailed: true }).nullable(),
});
export const manualCheckResultSchema = z.object({
    accepted: z.boolean(),
    runId: uuidSchema.nullable(),
    status: z.enum(['queued', 'succeeded', 'partial', 'failed']),
    offersSucceeded: z.number().int().nonnegative(),
    offersFailed: z.number().int().nonnegative(),
    message: z.string(),
});
// Broad searches are separate entities, never interchangeable with exact products.
export const watchQueryInputSchema = z.object({
    query: z.string().trim().min(3, 'Opisz, czego szukasz (co najmniej 3 znaki).').max(160),
    manufacturer: z.string().trim().max(120).default(''),
    manufacturers: z.array(z.string().trim().min(2).max(120)).max(10).optional(),
    keyFilters: z.array(z.string().trim().min(2).max(40)).max(8).optional(),
    maxPriceMinor: optionalAmountSchema.default(null),
    selectedStores: z.array(storeKeySchema).min(1, 'Wybierz przynajmniej jeden sklep.').max(6)
        .refine(values => new Set(values).size === values.length, 'Sklepy nie mogą się powtarzać.'),
});
export const queryResultSchema = z.object({
    id: uuidSchema, storeKey: storeKeySchema, url: z.string().url(), name: z.string(),
    imageUrl: z.string().url().nullable().optional(),
    currentPriceMinor: z.number().int().positive().nullable(), currency: currencySchema,
    availability: availabilitySchema, status: z.enum(['pending', 'ok', 'blocked', 'error', 'not_matching', 'stale']),
    message: z.string().nullable(), lastCheckedAt: z.string().datetime().nullable(),
    lastSuccessfulAt: z.string().datetime().nullable(), manual: z.boolean(), history: z.array(pricePointSchema),
});
export const watchQuerySchema = watchQueryInputSchema.extend({
    id: uuidSchema, kind: z.literal('query'), isDemo: z.boolean(), createdAt: z.string().datetime(),
    sources: z.array(z.object({ storeKey: storeKeySchema, status: z.enum(['pending', 'checking', 'ok', 'not_found', 'blocked', 'error']),
        lastSearchedAt: z.string().datetime().nullable(), message: z.string().nullable() })),
    results: z.array(queryResultSchema),
});
export const queryLinkSchema = z.object({ storeKey: storeKeySchema, url: z.string().url().max(2048) });
export const urlImportInputSchema = z.object({ url: z.string().url().max(2048), categoryId: uuidSchema.optional(), targetPriceMinor: optionalAmountSchema.default(null) }).refine(input => Boolean(detectStore(input.url)), 'Wklej bezpośredni link HTTPS z obsługiwanego sklepu.');
export const urlImportSchema = z.object({ id: uuidSchema, url: z.string().url(), storeKey: storeKeySchema, status: z.enum(['pending', 'checking', 'done', 'blocked', 'error']), message: z.string().nullable(), productId: uuidSchema.nullable(), createdAt: z.string().datetime() });
export const watchResultInputSchema = z.object({ categoryId: uuidSchema.optional(), targetPriceMinor: optionalAmountSchema.default(null) });
export const emailSettingsInputSchema = z.object({ enabled: z.boolean(), recipient: z.string().trim().email('Podaj poprawny adres e-mail.').max(320), onDrop: z.boolean(), onTarget: z.boolean(), onMinimum: z.boolean(), minDropPercent: z.number().min(0).max(100) }).refine(value => !value.enabled || value.onDrop || value.onTarget || value.onMinimum, 'Wybierz przynajmniej jeden warunek alertu.');
export const emailSettingsSchema = z.object({ settings: emailSettingsInputSchema, providerConfigured: z.boolean(), message: z.string(), history: z.array(z.object({ id: uuidSchema, status: z.enum(['pending', 'sent', 'failed', 'skipped']), createdAt: z.string().datetime(), sentAt: z.string().datetime().nullable(), message: z.string().nullable(), test: z.boolean() })) });
export const smartConditionSchema = z.enum(['below_price', 'drop_amount', 'drop_percent', 'minimum_30d', 'minimum_90d', 'back_in_stock', 'new_offer', 'better_offer']);
export const smartRuleInputSchema = z.object({ condition: smartConditionSchema, thresholdMinor: optionalAmountSchema.default(null), thresholdPercent: z.number().min(0.1).max(100).nullable().default(null), cooldownMinutes: z.number().int().min(1).max(43200).default(1440), enabled: z.boolean() }).superRefine((rule, ctx) => {
    if (rule.enabled && ['below_price', 'drop_amount'].includes(rule.condition) && rule.thresholdMinor === null)
        ctx.addIssue({ code: 'custom', message: 'Podaj kwotę alertu.', path: ['thresholdMinor'] });
    if (rule.enabled && rule.condition === 'drop_percent' && rule.thresholdPercent === null)
        ctx.addIssue({ code: 'custom', message: 'Podaj procent spadku.', path: ['thresholdPercent'] });
});
export const smartRulesInputSchema = z.object({ rules: z.array(smartRuleInputSchema).max(8).refine(rules => new Set(rules.map(rule => rule.condition)).size === rules.length, 'Każdy warunek może wystąpić tylko raz.') });
export const smartRuleSchema = smartRuleInputSchema.extend({ id: uuidSchema, channel: z.literal('email') });
